/* global process */

/**
 * Deliberately not using any material stuff here so it isn't loaded before we need it
 */

import * as PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from '@material-ui/core';
import { useAuth0 } from 'components/Auth/react-auth0-wrapper';
import { themeBrandingTokens } from 'theme/palette';

function delete_cookie(name) {
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

const AU_SERVERS = [
  'dev.adgile.media',
  'dev.adgile.com',
  'dev.catalyst.adgile.com',
  'stage.adgile.media',
  'stage.adgile.com',
  'stage.catalyst.adgile.com',
  'live.adgile.media',
  'live.adgile.com',
  'live.catalyst.adgile.com',
  'foresight.adgile.media',
  'portal.adgile.media',
  'au.portal.adgile.media',
  'catalyst.adgile.com',
  'au.catalyst.adgile.com',
];

function checkRegion(auth0) {
  if (window.location.hostname.toLowerCase() === 'localhost') return;
  const params = new URLSearchParams(window.location.search);
  if (params.has('ignore-region')) {
    return;
  }

  const user = auth0.user;
  let region = user['https://adgile.com/region'];
  if (!region) {
    region = user['https://adgile.media/region'];
  }
  if (!region) {
    region = 'AU';
  }
  region = region.toLowerCase();

  if (!window.location.hostname.toLowerCase().startsWith(`${region}.`)) {
    if (region !== 'au' || !AU_SERVERS.includes(window.location.hostname.toLowerCase())) {
      window.location.href = `https://${region}.catalyst.adgile.com`;
    }
  }
}

const PrivateBoundary = function ({ children }) {
  const auth0 = useAuth0();
  const { isAuthenticated, loginWithRedirect } = auth0;
  const [errorDescription, setErrorDescription] = useState(null);
  
  // NEW: State for our specific custom unverified flow
  const [isUnverified, setIsUnverified] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    let message = params.get('error_description') || error;

    // Attempt to parse JSON payload from the Auth0 Action
    if (error === 'access_denied' && message) {
      try {
        const parsedError = JSON.parse(message);
        
        if (parsedError.code === 'unverified_email') {
          setIsUnverified(true);
          setUserEmail(parsedError.email);
          message = null; // Nullify standard message to prevent it from rendering
        }
      } catch (e) {
        // Not a JSON string. Fall through to standard string error handling.
      }
    }

    // Standard fallback mapping
    switch (message) {
      case 'ESOCKETTIMEDOUT':
        message = 'There was an error contacting the server. Please try again.';
        break;
    }
    
    setErrorDescription(message);

    if (isAuthenticated) {
      checkRegion(auth0);
    } else if (!error) {
      try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          if (cookie.startsWith(' a0.spajs.txs.')) {
            const name = cookie.split('=', 1)[0];
            delete_cookie(name);
          }
        }

        loginWithRedirect({
          appState: { targetUrl: location.href },
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, [isAuthenticated, loginWithRedirect]);

  const handleOnClick = useCallback(e => {
    e.preventDefault();
    auth0.logout({
      returnTo: window.location.origin,
      client_id: process.env.CLIENT_ID,
    });
    return false;
  }, [auth0]);

  return (
    <Fragment>
      {isAuthenticated ? (
        children
      ) : (
        <div className="splash">
          <div>
            <img
              src={themeBrandingTokens.catalystLightLogo}
              width="300px"
            />
          </div>
          
          {/* Render custom UI if the unverified email payload was detected */}
          {isUnverified ? (
            <Fragment>
              <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                <p>Please verify your email.</p>
                <p>You're almost there! We've sent an email to</p>
                <p><strong>{userEmail}</strong></p>
                <p>
                  Just click on the link in that email to complete your verification. If you don't see it, you may need to check your spam folder. Once you've verified, click the retry button.
                </p>
                <div>
                  <Link
                    href="/"
                    onClick={handleOnClick}
                    color="inherit"
                    underline="always"
                  >
                    Go Back
                  </Link>{' '}
                  |{' '}
                  <Link
                    color="inherit"
                    href="/"
                    underline="always"
                  >
                    Retry
                  </Link>
                </div>
              </div>
            </Fragment>
          // Original fallback for standard errors
          ) : errorDescription ? (
            <Fragment>
              <div>{errorDescription}</div>
              <div>
                <Link
                  href="/"
                  onClick={handleOnClick}
                  color="inherit"
                >
                  Logout
                </Link>{' '}
                |{' '}
                <Link
                  color="inherit"
                  href="/"
                >
                  Retry
                </Link>
              </div>
            </Fragment>
          ) : (
            <div>LOADING ...</div>
          )}
        </div>
      )}
    </Fragment>
  );
};

PrivateBoundary.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
};

export default PrivateBoundary;
